/**
 * ContestsAdmin — Manager-only admin card for editing the May Contest
 * config (name, prize, dates, % tiers).
 *
 * Previously also hosted Heart Balloons admin; that section was removed
 * 2026-05-19 with the campaign cleanup per user request. Re-instate via
 * `git revert` if a future Mother's Day cycle wants it back.
 *
 * Endpoints used:
 *   GET/PUT  /api/contest/may-2026/config
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Trophy, Plus, X as XIcon } from "@phosphor-icons/react";
import { Button } from "../components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;


export default function ContestsAdmin({ user }) {
  const actorId = user?.crew_id || "";
  const [contest, setContest] = useState(null);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState({});

  useEffect(() => {
    const load = async () => {
      try {
        const r = await axios.get(`${API}/contest/may-2026/config`);
        setContest(r.data);
      } catch (e) {
        console.error("[ContestsAdmin] load failed", e);
      }
    };
    load();
  }, []);

  const startEdit = () => {
    setDraft({
      ...contest,
      // Deep-clone tiers so cancels don't mutate the source array.
      tiers: (contest?.tiers || []).map((t) => ({ ...t })),
    });
    setEditing(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const r = await axios.put(`${API}/contest/may-2026/config`, {
        actor_crew_id: actorId,
        name: draft.name,
        starts: draft.starts,
        ends: draft.ends,
        prize_per_crew: Number(draft.prize_per_crew),
        winning_stores_per_region: Number(draft.winning_stores_per_region),
        total_winning_stores: Number(draft.total_winning_stores),
        tile_visible: !!draft.tile_visible,
        tiers: (draft.tiers || []).map((t) => ({
          min_pct: Number(t.min_pct),
          tickets: Number(t.tickets),
          label: String(t.label || ""),
        })),
      });
      setContest(r.data);
      setEditing(false);
      toast.success("Email Contest settings saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const cancel = () => { setEditing(false); setDraft({}); };

  const setField = (k, v) => setDraft((d) => ({ ...d, [k]: v }));
  const setTier = (i, k, v) => setDraft((d) => {
    const tiers = (d.tiers || []).map((t, idx) => idx === i ? { ...t, [k]: v } : t);
    return { ...d, tiers };
  });
  const addTier = () => setDraft((d) => ({ ...d, tiers: [...(d.tiers || []), { min_pct: 0, tickets: 0, label: "" }] }));
  const removeTier = (i) => setDraft((d) => ({ ...d, tiers: (d.tiers || []).filter((_, idx) => idx !== i) }));

  return (
    <>
    <div className="dashboard-card p-0 overflow-hidden" data-testid="contests-admin-card">
      <div className="px-5 pt-5 pb-3 border-b border-slate-100 flex items-center gap-2">
        <Trophy weight="fill" className="w-5 h-5 text-amber-500" />
        <h3 className="text-base font-black text-slate-900" style={{ fontFamily: "'Five Below Gothic', Chivo, sans-serif" }}>
          Email Contest
        </h3>
      </div>

      <section className="p-5" data-testid="contests-admin-contest">
        {!contest ? (
          <p className="text-sm text-slate-400">Loading…</p>
        ) : !editing ? (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <p className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Prize</p>
                <p className="font-black text-slate-900 tabular-nums">${contest.prize_per_crew}/crew</p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Window</p>
                <p className="font-bold text-slate-700">{contest.starts} → {contest.ends}</p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Winners</p>
                <p className="font-bold text-slate-700">{contest.winning_stores_per_region}/region · {contest.total_winning_stores} total</p>
              </div>
            </div>
            {!!(contest.tiers || []).length && (
              <div>
                <p className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">Tiers</p>
                <ul className="text-xs space-y-0.5">
                  {(contest.tiers || []).map((t, i) => (
                    <li key={i} className="flex justify-between gap-2 text-slate-700">
                      <span className="font-bold">{(t.min_pct * 100).toFixed(0)}%+</span>
                      <span>{t.label} · {t.tickets} tickets</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <div className="flex items-center gap-2 pt-1" data-testid="contest-tile-visibility">
              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider ${contest.tile_visible ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                {contest.tile_visible ? "● Home tile ON" : "○ Home tile HIDDEN"}
              </span>
              <span className="text-[11px] text-slate-500">
                {contest.tile_visible
                  ? "Crew see this contest on Home"
                  : "Flip ON when the next contest is ready to launch"}
              </span>
            </div>
            <div className="pt-1">
              <Button variant="outline" size="sm" onClick={startEdit} data-testid="contest-edit-button">
                Edit
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3 text-xs">
              <label className="block">
                <span className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Name</span>
                <input value={draft.name || ""} onChange={(e) => setField("name", e.target.value)} className="w-full h-9 rounded border border-slate-200 px-2 text-sm mt-1" />
              </label>
              <label className="block">
                <span className="text-[10px] uppercase tracking-wider font-bold text-slate-500">$ / crew</span>
                <input type="number" value={draft.prize_per_crew ?? 0} onChange={(e) => setField("prize_per_crew", e.target.value)} className="w-full h-9 rounded border border-slate-200 px-2 text-sm mt-1" />
              </label>
              <label className="block">
                <span className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Starts</span>
                <input type="date" value={draft.starts || ""} onChange={(e) => setField("starts", e.target.value)} className="w-full h-9 rounded border border-slate-200 px-2 text-sm mt-1" />
              </label>
              <label className="block">
                <span className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Ends</span>
                <input type="date" value={draft.ends || ""} onChange={(e) => setField("ends", e.target.value)} className="w-full h-9 rounded border border-slate-200 px-2 text-sm mt-1" />
              </label>
              <label className="block">
                <span className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Winners / region</span>
                <input type="number" value={draft.winning_stores_per_region ?? 0} onChange={(e) => setField("winning_stores_per_region", e.target.value)} className="w-full h-9 rounded border border-slate-200 px-2 text-sm mt-1" />
              </label>
              <label className="block">
                <span className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Total winners</span>
                <input type="number" value={draft.total_winning_stores ?? 0} onChange={(e) => setField("total_winning_stores", e.target.value)} className="w-full h-9 rounded border border-slate-200 px-2 text-sm mt-1" />
              </label>
            </div>
            {/* Home-tile visibility toggle — flip OFF between contests so
                the tile stops cluttering the home grid with stale copy. */}
            <label className="flex items-center justify-between gap-3 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5" data-testid="contest-tile-visible-toggle">
              <span className="min-w-0">
                <span className="block text-[11px] font-black uppercase tracking-wider text-slate-700">Show on Home</span>
                <span className="block text-[11px] text-slate-500 leading-snug">
                  ON = the Email Contest tile appears on every crew's Home screen. OFF = hide it (use between contests).
                </span>
              </span>
              <input
                type="checkbox"
                checked={!!draft.tile_visible}
                onChange={(e) => setField("tile_visible", e.target.checked)}
                className="w-5 h-5 accent-amber-500"
              />
            </label>
            <div>
              <div className="flex items-center justify-between mb-2">
                <p className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Tiers</p>
                <Button variant="ghost" size="sm" onClick={addTier} className="text-xs h-7"><Plus weight="bold" className="w-3 h-3 mr-1" /> Add tier</Button>
              </div>
              <div className="space-y-1.5">
                {(draft.tiers || []).map((t, i) => (
                  <div key={i} className="grid grid-cols-12 gap-1 items-center">
                    <input type="number" step="0.05" value={t.min_pct ?? 0} onChange={(e) => setTier(i, "min_pct", e.target.value)} className="col-span-3 h-8 rounded border border-slate-200 px-2 text-xs" placeholder="0.30" />
                    <input type="number" value={t.tickets ?? 0} onChange={(e) => setTier(i, "tickets", e.target.value)} className="col-span-3 h-8 rounded border border-slate-200 px-2 text-xs" placeholder="Tickets" />
                    <input value={t.label || ""} onChange={(e) => setTier(i, "label", e.target.value)} className="col-span-5 h-8 rounded border border-slate-200 px-2 text-xs" placeholder="Bronze" />
                    <Button variant="ghost" size="sm" onClick={() => removeTier(i)} className="col-span-1 h-8 p-0"><XIcon weight="bold" className="w-3.5 h-3.5 text-rose-500" /></Button>
                  </div>
                ))}
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 pt-2">
              <Button variant="ghost" size="sm" onClick={cancel} disabled={saving}>Cancel</Button>
              <Button size="sm" onClick={save} disabled={saving} className="fb-btn-primary" data-testid="contest-save-button">
                {saving ? "Saving…" : "Save"}
              </Button>
            </div>
          </div>
        )}
      </section>
    </div>
    <TicketEntryCard user={user} />
    </>
  );
}

// ---- Manual ticket entry (per-week, per-store) ----------------------------
// Lets the SM log the official ticket count they got from corporate via email
// for any week the app didn't compute (sister stores joined the contest mid-
// stream so prior-week tallies don't exist locally). Saving here overrides
// the auto-computed ticket count on the contest leaderboard + ticket card.
function TicketEntryCard({ user }) {
  const actorId = user?.crew_id || "";
  const storeId = user?.home_store_id || "";
  const isManager = !!user?.is_manager;
  const [contest, setContest] = useState(null);
  const [overrides, setOverrides] = useState({}); // week_num → row
  const [drafts, setDrafts] = useState({});       // week_num → { tickets, notes }
  const [saving, setSaving] = useState(0);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!storeId) return;
    let alive = true;
    const load = async () => {
      try {
        const [c, ov] = await Promise.all([
          axios.get(`${API}/contest/may-2026`, { params: { store_id: storeId } }),
          axios.get(`${API}/contest/may-2026/overrides`, { params: { store_id: storeId } }),
        ]);
        if (!alive) return;
        setContest(c.data);
        const map = {};
        for (const row of ov.data || []) map[row.week_num] = row;
        setOverrides(map);
        // Seed drafts from existing overrides so the form shows whatever
        // they previously entered.
        const seed = {};
        for (const w of c.data?.weeks || []) {
          const r = map[w.num];
          seed[w.num] = {
            tickets: r ? String(r.tickets) : "",
            notes: r?.notes || "",
          };
        }
        setDrafts(seed);
        setLoaded(true);
      } catch (e) {
        console.error("[TicketEntryCard] load failed", e);
      }
    };
    load();
    return () => { alive = false; };
  }, [storeId]);

  if (!isManager || !loaded || !contest) return null;

  const setDraft = (weekNum, field, value) => {
    setDrafts((d) => ({ ...d, [weekNum]: { ...(d[weekNum] || {}), [field]: value } }));
  };

  const saveWeek = async (weekNum) => {
    const d = drafts[weekNum] || {};
    const tickets = parseInt(d.tickets, 10);
    if (Number.isNaN(tickets) || tickets < 0 || tickets > 10) {
      toast.error("Tickets must be 0–10");
      return;
    }
    setSaving(weekNum);
    try {
      await axios.put(`${API}/contest/may-2026/overrides`, {
        actor_crew_id: actorId,
        store_id: storeId,
        week_num: weekNum,
        tickets,
        notes: d.notes || "",
      });
      // Refresh overrides + contest so the rest of the dashboard reflects it.
      const [c, ov] = await Promise.all([
        axios.get(`${API}/contest/may-2026`, { params: { store_id: storeId } }),
        axios.get(`${API}/contest/may-2026/overrides`, { params: { store_id: storeId } }),
      ]);
      setContest(c.data);
      const map = {};
      for (const row of ov.data || []) map[row.week_num] = row;
      setOverrides(map);
      toast.success(`Week ${weekNum} · ${tickets} ticket${tickets === 1 ? "" : "s"} saved`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save");
    } finally {
      setSaving(0);
    }
  };

  const clearWeek = async (weekNum) => {
    if (!window.confirm("Remove your manual entry for this week? The auto-computed ticket count will take over again.")) return;
    setSaving(weekNum);
    try {
      await axios.delete(`${API}/contest/may-2026/overrides/${weekNum}`, {
        params: { store_id: storeId, actor_crew_id: actorId },
      });
      const [c, ov] = await Promise.all([
        axios.get(`${API}/contest/may-2026`, { params: { store_id: storeId } }),
        axios.get(`${API}/contest/may-2026/overrides`, { params: { store_id: storeId } }),
      ]);
      setContest(c.data);
      const map = {};
      for (const row of ov.data || []) map[row.week_num] = row;
      setOverrides(map);
      setDrafts((p) => ({ ...p, [weekNum]: { tickets: "", notes: "" } }));
      toast.success(`Week ${weekNum} cleared`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't clear");
    } finally {
      setSaving(0);
    }
  };

  return (
    <div className="dashboard-card p-0 overflow-hidden mt-4" data-testid="ticket-entry-card">
      <div className="px-5 pt-5 pb-3 border-b border-slate-100">
        <div className="flex items-center gap-2 mb-1">
          <Trophy weight="duotone" className="w-5 h-5 text-blue-500" />
          <h3 className="text-base font-black text-slate-900" style={{ fontFamily: "'Five Below Gothic', Chivo, sans-serif" }}>
            Manual Ticket Entry
          </h3>
        </div>
        <p className="text-[12px] text-slate-500 leading-snug">
          Got an email from corporate with your weekly ticket count? Log it here. Saved totals override what the app computes from tallies — useful when your store joined the contest mid-stream and prior weeks weren't tracked in the app.
        </p>
      </div>

      <div className="divide-y divide-slate-100">
        {(contest.weeks || []).map((w) => {
          const draft = drafts[w.num] || { tickets: "", notes: "" };
          const existing = overrides[w.num];
          const isOverridden = !!existing;
          const dirty = (draft.tickets ?? "") !== (existing ? String(existing.tickets) : "")
            || (draft.notes || "") !== (existing?.notes || "");
          return (
            <div key={w.num} className="px-5 py-3" data-testid={`ticket-entry-week-${w.num}`}>
              <div className="flex items-center justify-between gap-2 mb-2">
                <div className="min-w-0">
                  <p className="font-black text-sm text-slate-900">
                    Week {w.num}
                    <span className="ml-1.5 text-[11px] font-semibold text-slate-400">{w.start} → {w.end}</span>
                  </p>
                  <p className="text-[11px] text-slate-500">
                    App computed: <span className="font-bold text-slate-700 tabular-nums">{(isOverridden ? existing.computed_tickets : w.tickets) ?? 0}</span>
                    {isOverridden && (
                      <span className="ml-2 inline-flex items-center gap-1 text-[10px] font-bold text-emerald-700 bg-emerald-100 px-1.5 py-0.5 rounded-full">
                        Override · {existing.tickets}
                      </span>
                    )}
                  </p>
                </div>
                <span className={`text-[10px] uppercase tracking-wider font-black px-2 py-0.5 rounded-full ${
                  w.status === "completed" ? "bg-slate-100 text-slate-600" :
                  w.status === "active" ? "bg-blue-100 text-blue-700" :
                  "bg-amber-100 text-amber-700"
                }`}>
                  {w.status === "completed" ? "Locked" : w.status === "active" ? "This week" : "Upcoming"}
                </span>
              </div>
              <div className="grid grid-cols-12 gap-2 items-center">
                <input
                  type="number"
                  min="0"
                  max="10"
                  inputMode="numeric"
                  value={draft.tickets}
                  onChange={(e) => setDraft(w.num, "tickets", e.target.value)}
                  placeholder="0"
                  className="col-span-3 h-9 rounded border border-slate-200 px-2 text-sm font-bold tabular-nums text-center"
                  data-testid={`ticket-entry-input-${w.num}`}
                />
                <input
                  value={draft.notes || ""}
                  onChange={(e) => setDraft(w.num, "notes", e.target.value)}
                  placeholder="Optional note (e.g., from corporate email 5/14)"
                  className="col-span-6 h-9 rounded border border-slate-200 px-2 text-xs"
                  data-testid={`ticket-entry-notes-${w.num}`}
                />
                <Button
                  size="sm"
                  onClick={() => saveWeek(w.num)}
                  disabled={saving === w.num || !dirty || draft.tickets === ""}
                  className="col-span-2 h-9 fb-btn-primary"
                  data-testid={`ticket-entry-save-${w.num}`}
                >
                  {saving === w.num ? "Saving…" : (isOverridden ? "Update" : "Save")}
                </Button>
                {isOverridden && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => clearWeek(w.num)}
                    disabled={saving === w.num}
                    className="col-span-1 h-9 p-0 text-rose-500 hover:text-rose-700"
                    title="Clear override"
                    data-testid={`ticket-entry-clear-${w.num}`}
                  >
                    <XIcon weight="bold" className="w-4 h-4" />
                  </Button>
                )}
              </div>
              {existing?.entered_by_name && (
                <p className="text-[10px] text-slate-400 mt-1.5">
                  Logged by {existing.entered_by_name} · {new Date(existing.entered_at).toLocaleDateString()}
                  {existing.notes && <span className="ml-1">· "{existing.notes}"</span>}
                </p>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
